import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Fab,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Avatar,
  Divider,
  IconButton,
  Tooltip,
  Pagination,
  Stack
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  ShoppingCart as ShoppingCartIcon,
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Category as CategoryIcon,
  LocalOffer as PriceIcon,
  Inventory as StockIcon
} from '@mui/icons-material';

const ProductLibrary = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [openCopyDialog, setOpenCopyDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  // Mock product library data
  const productLibrary = [
    {
      id: 1,
      name: 'টাজা টমেটো',
      category: 'সবজি',
      price: 120,
      unit: 'কেজি',
      image: '/images/groceries/tomato.webp',
      description: 'তাজা লাল টমেটো, ভিটামিন সি সমৃদ্ধ',
      tags: ['জৈব', 'তাজা', 'স্বাস্থ্যকর'],
      inStock: true,
      rating: 4.5,
      adminCreated: true
    },
    {
      id: 2,
      name: 'দেশি পেঁয়াজ',
      category: 'সবজি',
      price: 80,
      unit: 'কেজি',
      image: '/images/groceries/peyaj.jpeg',
      description: 'দেশি লাল পেঁয়াজ, রান্নার জন্য উপযুক্ত',
      tags: ['দেশি', 'তাজা'],
      inStock: true,
      rating: 4.2,
      adminCreated: true
    },
    {
      id: 3,
      name: 'বাসমতি চাল',
      category: 'চাল ও ডাল',
      price: 180,
      unit: 'কেজি',
      image: '/images/groceries/basmati.jpg',
      description: 'উচ্চমানের বাসমতি চাল, সুগন্ধযুক্ত',
      tags: ['বাসমতি', 'প্রিমিয়াম', 'সুগন্ধি'],
      inStock: true,
      rating: 4.8,
      adminCreated: true
    },
    {
      id: 4,
      name: 'মুরগির মাংস',
      category: 'মাংস ও মাছ',
      price: 350,
      unit: 'কেজি',
      image: '/images/groceries/murgi.jpeg',
      description: 'তাজা ব্রয়লার মুরগির মাংস',
      tags: ['ব্রয়লার', 'তাজা', 'হালাল'],
      inStock: true,
      rating: 4.6,
      adminCreated: true
    },
    {
      id: 5,
      name: 'আম (হিমসাগর)',
      category: 'ফল',
      price: 250,
      unit: 'কেজি',
      image: '/images/groceries/am.jpeg',
      description: 'মিষ্টি হিমসাগর আম, গ্রীষ্মকালীন ফল',
      tags: ['হিমসাগর', 'মিষ্টি', 'সিজনাল'],
      inStock: true,
      rating: 4.9,
      adminCreated: true
    },
    {
      id: 6,
      name: 'গরুর দুধ',
      category: 'দুগ্ধজাত',
      price: 65,
      unit: 'লিটার',
      image: '/images/groceries/দ-ধ.jpg',
      description: 'খাঁটি গরুর দুধ, পুষ্টিকর',
      tags: ['খাঁটি', 'পুষ্টিকর', 'দৈনিক'],
      inStock: true,
      rating: 4.4,
      adminCreated: true
    }
  ];

  const categories = ['সব', 'সবজি', 'ফল', 'চাল ও ডাল', 'মাংস ও মাছ', 'দুগ্ধজাত', 'মশলা'];

  // Filter products based on search and category
  const filteredProducts = productLibrary.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === '' || selectedCategory === 'সব' || 
                           product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProducts = filteredProducts.slice(startIndex, endIndex);

  const handleProductSelect = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === currentProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(currentProducts.map(p => p.id));
    }
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    // Clear selections when changing pages
    setSelectedProducts([]);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(event.target.value);
    setCurrentPage(1); // Reset to first page
    setSelectedProducts([]); // Clear selections
  };

  // Reset pagination when search or category changes
  React.useEffect(() => {
    setCurrentPage(1);
    setSelectedProducts([]);
  }, [searchTerm, selectedCategory]);

  const handleCopyProducts = () => {
    setOpenCopyDialog(true);
  };

  const confirmCopyProducts = () => {
    // Here you would typically make an API call to copy products
    console.log('Copying products:', selectedProducts);
    
    setSnackbar({
      open: true,
      message: `${selectedProducts.length}টি পণ্য সফলভাবে আপনার স্টোরে কপি করা হয়েছে`,
      severity: 'success'
    });
    
    setSelectedProducts([]);
    setOpenCopyDialog(false);
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 0 } }}>
      {/* Header */}
      <Box sx={{ mb: { xs: 2, md: 4 } }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 600, 
            mb: 1,
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
          }}
        >
          পণ্য লাইব্রেরি
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
        গ্রামীণ বাজারের পণ্য লাইব্রেরি থেকে সহজ ও ঝামেলামুক্তভাবে আপনার দোকানে পণ্য যোগ করুন। পছন্দসই পণ্য নির্বাচন করুন এবং কপি করুন। প্রয়োজনে সেগুলো সম্পাদনা করুন।
        </Typography>
      </Box>

      {/* Search and Filter Controls */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, md: 4 } }}>
        <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              placeholder="পণ্য খুঁজুন..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="medium"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="medium">
              <InputLabel>ক্যাটেগরি</InputLabel>
              <Select
                value={selectedCategory}
                label="ক্যাটেগরি"
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedProducts.length === currentProducts.length && currentProducts.length > 0}
                  indeterminate={selectedProducts.length > 0 && selectedProducts.length < currentProducts.length}
                  onChange={handleSelectAll}
                />
              }
              label="সব নির্বাচন করুন"
              sx={{ 
                '& .MuiFormControlLabel-label': { 
                  fontSize: { xs: '0.875rem', sm: '1rem' } 
                } 
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Box sx={{ textAlign: { xs: 'left', md: 'center' } }}>
              <Typography variant="body2" color="text.secondary">
                মোট: {filteredProducts.length}টি পণ্য
              </Typography>
              <Typography variant="body2" color="text.secondary">
                পৃষ্ঠা: {currentPage} / {totalPages}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={1}>
            {selectedProducts.length > 0 && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<CopyIcon />}
                onClick={handleCopyProducts}
                fullWidth
                size={window.innerWidth < 600 ? "small" : "medium"}
                sx={{
                  bgcolor: 'primary.main',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  '&:hover': {
                    bgcolor: 'primary.dark'
                  }
                }}
              >
                কপি ({selectedProducts.length})
              </Button>
            )}
          </Grid>
        </Grid>
        
        {/* Items Per Page Selector */}
        <Box sx={{ 
          mt: 2, 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 2, sm: 0 }
        }}>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
            <InputLabel>প্রতি পৃষ্ঠায়</InputLabel>
            <Select
              value={itemsPerPage}
              label="প্রতি পৃষ্ঠায়"
              onChange={handleItemsPerPageChange}
            >
              <MenuItem value={8}>৮টি পণ্য</MenuItem>
              <MenuItem value={12}>১২টি পণ্য</MenuItem>
              <MenuItem value={16}>১৬টি পণ্য</MenuItem>
              <MenuItem value={24}>২৪টি পণ্য</MenuItem>
            </Select>
          </FormControl>
          
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              textAlign: { xs: 'center', sm: 'right' },
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }}
          >
            {startIndex + 1}-{Math.min(endIndex, filteredProducts.length)} এর মধ্যে {filteredProducts.length}টি পণ্য দেখাচ্ছে
          </Typography>
        </Box>
        
        {/* Selected Products Summary */}
        {selectedProducts.length > 0 && (
          <Box sx={{ 
            mt: 3, 
            p: 2, 
            bgcolor: '#e3f2fd', 
            borderRadius: 1, 
            border: '1px solid #2196f3' 
          }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between', 
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: { xs: 2, sm: 1 }
            }}>
              <Typography 
                variant="body2" 
                color="primary" 
                sx={{ 
                  fontWeight: 600,
                  textAlign: { xs: 'center', sm: 'left' }
                }}
              >
                {selectedProducts.length}টি পণ্য নির্বাচিত
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1 
              }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSelectedProducts([])}
                  fullWidth={window.innerWidth < 600}
                >
                  সব বাতিল করুন
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<CopyIcon />}
                  onClick={handleCopyProducts}
                  fullWidth={window.innerWidth < 600}
                >
                  নির্বাচিত পণ্য কপি করুন
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Products Grid */}
      <Box sx={{ px: { xs: 2, sm: 3 } }}>
        <Grid 
          container 
          spacing={{ xs: 2, sm: 3 }}
          sx={{
            display: 'grid',
            gap: { xs: '16px', sm: '24px' },
            gridTemplateColumns: {
              xs: 'repeat(1, 1fr)',     // 1 column on mobile
              sm: 'repeat(2, 1fr)',     // 2 columns on small tablets
              md: 'repeat(4, 1fr)',     // 4 columns on medium screens
              lg: 'repeat(6, 1fr)',     // 6 columns on large screens
              xl: 'repeat(8, 1fr)',     // 8 columns on extra large screens
              '2xl': 'repeat(8, 1fr)'   // 8 columns on 2xl screens
            }
          }}
        >
          {currentProducts.map((product) => (
            <Box key={product.id}>
              <Card 
                sx={{ 
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  border: selectedProducts.includes(product.id) ? '2px solid #1976d2' : '1px solid #e0e0e0',
                  '&:hover': {
                    boxShadow: 3,
                    transform: { xs: 'none', sm: 'translateY(-2px)' }, // No transform on mobile
                    transition: 'all 0.3s ease'
                  }
                }}
              >
                {/* Selection Checkbox */}
                <Checkbox
                  checked={selectedProducts.includes(product.id)}
                  onChange={() => handleProductSelect(product.id)}
                  sx={{
                    position: 'absolute',
                    top: { xs: 4, sm: 8 },
                    left: { xs: 4, sm: 8 },
                    zIndex: 1,
                    bgcolor: 'rgba(255,255,255,0.8)',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)'
                    }
                  }}
                />

                {/* Admin Badge */}
                <Chip
                  label="প্রশাসক"
                  color="primary"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: { xs: 4, sm: 8 },
                    right: { xs: 4, sm: 8 },
                    zIndex: 1,
                    fontSize: { xs: '0.6rem', sm: '0.75rem' }
                  }}
                />

                {/* Stock Status Badge */}
                <Chip
                  label={product.inStock ? "স্টকে আছে" : "স্টক নেই"}
                  color={product.inStock ? "success" : "error"}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: { xs: 40, sm: 48 },
                    right: { xs: 4, sm: 8 },
                    zIndex: 1,
                    fontSize: { xs: '0.6rem', sm: '0.75rem' }
                  }}
                />

                <CardMedia
                  component="img"
                  height={200}
                  image={product.image}
                  alt={product.name}
                  sx={{ 
                    objectFit: 'cover',
                    objectPosition: 'center',
                    flexShrink: 0,
                    width: '100%',
                    minHeight: 200,
                    maxHeight: 200,
                    backgroundColor: '#f5f5f5'
                  }}
                />

                <CardContent sx={{ 
                  flexGrow: 1, 
                  display: 'flex',
                  flexDirection: 'column',
                  p: { xs: 1.5, sm: 2 },
                  justifyContent: 'space-between'
                }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600, 
                        mb: 1,
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                        minHeight: { xs: 'auto', sm: '3rem' },
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {product.name}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CategoryIcon sx={{ fontSize: { xs: 14, sm: 16 }, mr: 0.5, color: 'text.secondary' }} />
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        {product.category}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PriceIcon sx={{ fontSize: { xs: 14, sm: 16 }, mr: 0.5, color: 'text.secondary' }} />
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        ৳{product.price} / {product.unit}
                      </Typography>
                    </Box>

                    {/* Stock Status with Icon */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <StockIcon sx={{ fontSize: { xs: 14, sm: 16 }, mr: 0.5, color: 'text.secondary' }} />
                      <Typography 
                        variant="body2" 
                        color={product.inStock ? "success.main" : "error.main"}
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          fontWeight: 500
                        }}
                      >
                        {product.inStock ? "স্টকে আছে" : "স্টক নেই"}
                      </Typography>
                    </Box>

                    <Typography 
                      variant="body2" 
                      sx={{ 
                        mb: 2, 
                        color: 'text.secondary',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        display: '-webkit-box',
                        WebkitLineClamp: { xs: 2, sm: 2 },
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {product.description}
                    </Typography>

                    {/* Tags */}
                    <Box sx={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: 0.5, 
                      mb: 2,
                      minHeight: { xs: '1.5rem', sm: '2rem' }
                    }}>
                      {product.tags.slice(0, 3).map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: { xs: '0.625rem', sm: '0.75rem' } }}
                        />
                      ))}
                    </Box>

                    {/* Rating */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          mr: 1,
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }}
                      >
                        রেটিং: {product.rating}
                      </Typography>
                      <Box sx={{ display: 'flex' }}>
                        {[...Array(5)].map((_, index) => (
                          <Typography
                            key={index}
                            sx={{
                              color: index < Math.floor(product.rating) ? '#ffc107' : '#e0e0e0',
                              fontSize: { xs: '0.875rem', sm: '1rem' }
                            }}
                          >
                            ★
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  </Box>

                  {/* Action Buttons - Always at bottom */}
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1,
                    mt: 'auto',
                    pt: 1
                  }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<VisibilityIcon />}
                      fullWidth
                      sx={{ 
                        whiteSpace: 'nowrap',
                        fontSize: { xs: '0.625rem', sm: '0.75rem' }
                      }}
                    >
                      বিস্তারিত
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<CopyIcon />}
                      fullWidth
                      onClick={() => {
                        setSelectedProducts([product.id]);
                        handleCopyProducts();
                      }}
                      sx={{ 
                        whiteSpace: 'nowrap',
                        fontSize: { xs: '0.625rem', sm: '0.75rem' }
                      }}
                    >
                      কপি করুন
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Grid>
      </Box>

      {/* No products found */}
      {filteredProducts.length === 0 && (
        <Box sx={{ px: { xs: 2, sm: 3 } }}>
          <Paper sx={{ 
            p: { xs: 4, sm: 6 }, 
            textAlign: 'center', 
            mt: { xs: 2, md: 4 } 
          }}>
            <Typography 
              variant="h6" 
              color="text.secondary" 
              gutterBottom
              sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              কোনো পণ্য পাওয়া যায়নি
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              অন্য কীওয়ার্ড দিয়ে খোঁজ করুন বা ক্যাটেগরি পরিবর্তন করুন
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Pagination */}
      {filteredProducts.length > 0 && (
        <Paper sx={{ p: { xs: 2, sm: 3 }, mt: { xs: 2, md: 4 } }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            flexDirection: 'column', 
            gap: 2 
          }}>
            {/* Page Info */}
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                textAlign: 'center',
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }}
            >
              পৃষ্ঠা {currentPage} এর {totalPages} - মোট {filteredProducts.length}টি পণ্য
            </Typography>
            
            {/* Pagination Component */}
            <Stack spacing={2}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size={window.innerWidth < 600 ? "small" : "large"}
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: { xs: '0.75rem', sm: '1rem' },
                    fontWeight: 500,
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    },
                  },
                }}
              />
            </Stack>
            
            {/* Page Navigation Info */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between', 
              alignItems: 'center', 
              width: '100%', 
              mt: 2,
              gap: { xs: 2, sm: 0 }
            }}>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ 
                  textAlign: { xs: 'center', sm: 'left' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}
              >
                {startIndex + 1}-{Math.min(endIndex, filteredProducts.length)} এর মধ্যে দেখাচ্ছে
              </Typography>
              
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
                <InputLabel>প্রতি পৃষ্ঠায় পণ্য</InputLabel>
                <Select
                  value={itemsPerPage}
                  label="প্রতি পৃষ্ঠায় পণ্য"
                  onChange={handleItemsPerPageChange}
                >
                  <MenuItem value={8}>৮টি পণ্য</MenuItem>
                  <MenuItem value={12}>১২টি পণ্য</MenuItem>
                  <MenuItem value={16}>১৬টি পণ্য</MenuItem>
                  <MenuItem value={24}>২৪টি পণ্য</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Floating Action Button for Selected Products */}
      {selectedProducts.length > 0 && (
        <Fab
          color="primary"
          onClick={handleCopyProducts}
          sx={{
            position: 'fixed',
            bottom: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
            zIndex: 1000,
            display: { xs: 'none', sm: 'flex' } // Hide on mobile to avoid conflicts
          }}
        >
          <Badge badgeContent={selectedProducts.length} color="error">
            <CopyIcon />
          </Badge>
        </Fab>
      )}

      {/* Copy Confirmation Dialog */}
      <Dialog open={openCopyDialog} onClose={() => setOpenCopyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          পণ্য কপি করার নিশ্চিতকরণ
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            আপনি {selectedProducts.length}টি পণ্য আপনার দোকানে কপি করতে চান?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            এই পণ্যগুলো আপনার পণ্য তালিকায় যুক্ত হবে এবং আপনি পরে সেগুলো সম্পাদনা করতে পারবেন।
          </Typography>
          
          {/* Selected Products Preview */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              নির্বাচিত পণ্যসমূহ:
            </Typography>
            <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
              {selectedProducts.map(productId => {
                const product = productLibrary.find(p => p.id === productId);
                return (
                  <Box key={productId} sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
                    <CheckCircleIcon color="primary" sx={{ mr: 1, fontSize: 20 }} />
                    <Typography variant="body2">
                      {product?.name} - ৳{product?.price}/{product?.unit}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCopyDialog(false)}>
            বাতিল
          </Button>
          <Button variant="contained" onClick={confirmCopyProducts}>
            কপি করুন
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductLibrary;
